import { createContext, useContext, useEffect, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { useAuth } from './AuthContext';
import { subscribeToUnreadNotifications, type InAppNotification } from '../services/inAppNotifications';
import { useToast } from '../hooks/useToast';
import { logger } from '../utils/logger';

interface NotificationsContextType {
  notifications: InAppNotification[];
  unreadCount: number;
}

const NotificationsContext = createContext<NotificationsContextType>({
  notifications: [],
  unreadCount: 0,
});

// eslint-disable-next-line react-refresh/only-export-components
export function useNotifications() {
  return useContext(NotificationsContext);
}

export function NotificationsProvider({ children }: { children: ReactNode }) {
  const { currentUser } = useAuth();
  const { showInfo, showSuccess } = useToast();
  const [notifications, setNotifications] = useState<InAppNotification[]>([]);
  const isFirstLoadRef = useRef(true);
  const knownIdsRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    if (!currentUser) {
      setNotifications([]); // eslint-disable-line react-hooks/set-state-in-effect
      isFirstLoadRef.current = true;
      knownIdsRef.current = new Set();
      return;
    }

    isFirstLoadRef.current = true;
    knownIdsRef.current = new Set();

    logger.info('Setting up notifications listener', { userId: currentUser.uid });

    const unsubscribe = subscribeToUnreadNotifications(
      currentUser.uid,
      (unreadNotifs) => {
        if (isFirstLoadRef.current) {
          // Seed known IDs on first load so we don't toast for existing notifications
          knownIdsRef.current = new Set(unreadNotifs.map((n) => n.id));
          isFirstLoadRef.current = false;
        } else {
          // Toast for genuinely new notifications.
          // Suppress toasts on onboarding routes — the welcome notification fires
          // immediately after signup (before the user has even entered their name),
          // and a popup during /onboarding/* steps is intrusive. The notification
          // is still tracked in knownIds so it doesn't re-fire later.
          const onOnboardingRoute = typeof window !== 'undefined' &&
            window.location.pathname.startsWith('/onboarding/');
          for (const n of unreadNotifs) {
            if (!knownIdsRef.current.has(n.id)) {
              knownIdsRef.current.add(n.id);
              if (onOnboardingRoute) continue;
              if (n.type === 'like' || n.type === 'follow') {
                showSuccess(n.message);
              } else {
                showInfo(n.message);
              }
            }
          }
          // Remove IDs that are no longer unread (marked as read)
          const currentIds = new Set(unreadNotifs.map((n) => n.id));
          for (const id of knownIdsRef.current) {
            if (!currentIds.has(id)) knownIdsRef.current.delete(id);
          }
        }
        setNotifications(unreadNotifs);
      },
      (error) => {
        logger.error('Error in notifications listener', { userId: currentUser.uid }, error);
      }
    );

    return () => {
      logger.info('Cleaning up notifications listener', { userId: currentUser.uid });
      unsubscribe();
    };
  }, [currentUser?.uid]);

  return (
    <NotificationsContext.Provider value={{ notifications, unreadCount: notifications.length }}>
      {children}
    </NotificationsContext.Provider>
  );
}
